<template>
  <div v-if="type === 'tel' && !phoneCountryReady" class="custom-input" />
  <ValidationProvider
    v-else
    :name="name"
    :rules="rules"
    :mode="type === 'tel' ? 'eager' : 'aggressive'"
    v-slot="{ errors }"
  >
    <div class="custom-input">
      <input
        v-if="['text', 'number', 'email'].includes(type)"
        :class="[
          'form-control',
          { 'no-border': !border },
          { 'input-error': errors.length },
        ]"
        :type="type"
        :name="inputName"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        v-model="inputValue"
        @click="$emit('click')"
        @blur="$emit('blur')"
      />
      <vue-tel-input
        v-else-if="type == 'tel' && phoneCountryReady"
        mode="international"
        :class="[
          'form-control',
          { 'no-border': !border },
          { 'input-error': errors.length },
        ]"
        v-model="inputValue"
        :disabled="disabled"
        :readonly="readonly"
        :default-country="phoneDefaultCountry"
        :auto-default-country="false"
        :preferred-countries="preferredPhoneCountries"
        :input-options="{ placeholder, name: inputName, autocomplete }"
        @country-changed="$emit('setCountry', $event)"
        @blur="$emit('blur')"
      />
      <textarea
        v-else-if="type == 'textarea'"
        :class="[
          'form-control',
          { 'no-border': !border },
          { 'input-error': errors.length },
        ]"
        :name="inputName"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        v-model="inputValue"
        :disabled="disabled"
        :readonly="readonly"
        rows="7"
      ></textarea>
      <select
        v-else-if="type == 'country'"
        :class="[
          'form-control',
          { 'no-border': !border },
          { 'input-error': errors.length },
        ]"
        :name="inputName"
        :autocomplete="autocomplete"
        v-model="inputValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        @change="setCompanyCountryCode"
      >
        <option value="">{{ $t("listing.country") }}</option>
        <option
          v-for="country in countryList"
          :key="country.name"
          :value="country.name"
        >
          {{ country.name }}
        </option>
      </select>
      <select
        v-else-if="type == 'single-select'"
        :class="[
          'form-control',
          { 'no-border': !border },
          { 'input-error': errors.length },
        ]"
        v-model="inputValue"
        :disabled="disabled"
        :readonly="readonly"
        :placeholder="placeholder"
        @change="$emit('change')"
      >
        <option value="">{{ placeholder }}</option>
        <template v-if="isArrayOfObjects(options)">
          <option v-for="{ id, text } in options" :key="id" :value="id">
            {{ text }}
          </option>
        </template>
        <template v-else>
          <option v-for="option in options" :key="option" :value="option">
            {{ option }}
          </option>
        </template>
      </select>
      <span v-if="errors.length" class="text-danger small">{{
        errors[0]
      }}</span>
    </div>
  </ValidationProvider>
</template>

<script>
import { countryList } from "../shared";
import { getDefaultPhoneCountryCode } from "../helpers";
import { ValidationProvider } from "vee-validate";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";

export default {
  name: "ValidatedInput",
  components: {
    ValidationProvider,
    VueTelInput,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    rules: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    name: {
      type: String,
      default: "",
    },
    inputName: {
      type: String,
      default: "",
    },
    autocomplete: {
      type: String,
      default: "",
    },
    border: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    defaultCountry: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      phoneDefaultCountry: "",
      phoneCountryReady: false,
      preferredPhoneCountries: ["DE", "AT", "CH", "NL", "FR", "IT", "GB", "US"],
    };
  },
  computed: {
    countryList: () => countryList,
    inputValue: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
  },
  async mounted() {
    if (this.type !== "tel") {
      return;
    }

    this.phoneDefaultCountry = this.defaultCountry || (await getDefaultPhoneCountryCode());
    this.phoneCountryReady = true;
  },
  methods: {
    isArrayOfObjects(array) {
      return (
        Array.isArray(array) &&
        array.every((item) => typeof item === "object" && item !== null)
      );
    },
    setCompanyCountryCode(e) {
      const value = e.target.value;
      if (value) {
        const country = this.countryList.find(({ name }) => name === value);
        this.$emit("setCountryCode", country?.code);
      }
    },
  },
};
</script>

<style scoped>
.input-error {
  border: 1px solid red !important;
}
@media screen and (max-width: 600px) {
  .form-control {
    font-size: 14px;
  }
}
</style>
